// FILECONTENT_START src/common/components/Inputs/File/ImageFile.tsx
import ClearIcon from "@mui/icons-material/Clear";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  alpha,
  Box,
  BoxProps,
  CircularProgress,
  FormHelperText,
  IconButton,
  IconButtonProps,
  Stack,
  StackProps,
  Tooltip,
  Typography,
} from "@mui/material";
import I18n from "i18n-js";
import { ReactNode } from "react";
import { previewFile } from "../../FilePreview";
import { UploadFileTypes, UploadIconButton } from "../../UploadButton";
import { FilePreviewType } from "./../../FilePreview/FilePreview";
import useFilePicker, { UseFilePickerProps } from "./useFilePicker";
export type ImageFileProps = {
  buttonSize?: IconButtonProps["size"];
  readOnly?: boolean;
  size?: number;
  fallBackComponent?: ReactNode;
  fallBackImage?: string;
  type?: FilePreviewType;
  placeholder?: string;
  helperText?: string;
  isDirty?: boolean;
  required?: boolean;
  accept?: string;
  onlyUploadButton?: boolean;
  showTolbar?: boolean;
  error?: any;
  stackProps?: StackProps;
  useObjectValue?: boolean;
} & Omit<BoxProps, "onChange" | "onClick" | "component" | "children" | "src"> &
  Omit<UseFilePickerProps, "useObjectValue">;
export default function ImageFile({
  upload,
  uri,
  value,
  onChange,
  buttonSize = "small",
  readOnly,
  showTolbar = true,
  size = 56,
  fallBackComponent,
  fallBackImage,
  type,
  placeholder,
  helperText,
  isDirty,
  required,
  validate,
  file: _file,
  onlyUploadButton,
  accept,
  stackProps,
  useObjectValue,
  error,
  ...boxProps
}: ImageFileProps) {
  const { file, downloadUrl, clearFile, onUploadButtonChange, loading } =
    useFilePicker({
      value: value as any,
      uri,
      upload,
      onChange,
      file: _file,
      validate,
      useObjectValue: (useObjectValue ? true : false) as any,
    });

  if (onlyUploadButton) {
    return (
      <UploadIconButton
        src={file?.uri}
        title={I18n.t("App.upload")}
        accept={accept || UploadFileTypes.DocumentsAndImages}
        onChange={onUploadButtonChange}
      >
        <CloudUploadIcon fontSize={buttonSize} />
      </UploadIconButton>
    );
  }
  return (
    <Box {...boxProps} sx={{ position: "relative", ...boxProps.sx }}>
      {!!file?.uri && <img src={file?.uri} style={{ width: "100%" }} />}
      {!file?.uri && fallBackComponent ? fallBackComponent : null}
      {!file?.uri && fallBackImage ? (
        <img src={fallBackImage} style={{ width: "100%" }} />
      ) : null}
      {showTolbar && (
        <Stack
          direction="row"
          spacing={1}
          {...stackProps}
          sx={{
            position: "absolute",
            //left: 0,
            borderBottomLeftRadius: "16px",
            right: 0,
            top: 0,
            alignItems: "center",
            backgroundColor: theme =>
              alpha(theme.palette.background.default, 0.6),
            ...stackProps?.sx,
          }}
        >
          {!!file?.uri && (
            <IconButton
              onClick={() =>
                !!file?.uri &&
                previewFile({
                  url: file?.uri,
                  title: file?.name,
                  fileName: file?.name,
                  type,
                })
              }
            >
              <VisibilityIcon />
            </IconButton>
          )}
          {!readOnly && (
            <>
              {!!file && (
                <Tooltip title={I18n.t("App.clear")}>
                  <IconButton size={buttonSize} onClick={clearFile}>
                    <ClearIcon fontSize={buttonSize} />
                  </IconButton>
                </Tooltip>
              )}
              {!file && !!placeholder && (
                <Typography color="text.secondary" sx={{ marginLeft: 1 }}>
                  {placeholder}
                </Typography>
              )}
              <UploadIconButton
                src={file?.uri}
                title={I18n.t("App.upload")}
                accept={accept || UploadFileTypes.DocumentsAndImages}
                onChange={onUploadButtonChange}
              >
                {loading ? (
                  <CircularProgress size={18} />
                ) : (
                  <CloudUploadIcon fontSize={buttonSize} />
                )}
              </UploadIconButton>
            </>
          )}
        </Stack>
      )}
      {!!(error || helperText) && (
        <FormHelperText error={!!error}>{error || helperText}</FormHelperText>
      )}
    </Box>
  );
}

// FILECONTENT_END src/common/components/Inputs/File/ImageFile.tsx
